import { Checkbox, Collapse, Popover, Select } from "antd";
import cuid from "cuid";
import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { DOCUMENT_STEPS } from "utils/constants";
import { DEFAULT_DOCUMENT_STEP_FLAGS } from "utils/helpers/documentStepsConfig";

const LISTED_USAGES = 2;

export const newUsage = () => ({
  key: cuid(),
  insurancePlanIds: [],
  ignoreOthers: false,
  exactMatch: false,
  steps: { ...DEFAULT_DOCUMENT_STEP_FLAGS },
});

export const completeUsages = (usages) =>
  usages.filter(({ insurancePlanIds }) => !isEmpty(insurancePlanIds));

export const usageText = (usage, planNames) =>
  usage.insurancePlanIds.map((id) => planNames[id] ?? id).join(" + ");

const popupContainer = (node) => node.parentNode;

const UsageEditor = ({ usage, plans, onChange, onRemove }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const USES = t("STATIC.USES", { returnObjects: true });
  const STEPS = t("STATIC.STEPS", { returnObjects: true });

  return (
    <div className="dynamic-documents-editor__use">
      <div className="dynamic-documents-editor__use-row">
        <Select
          mode="multiple"
          showSearch
          size="small"
          optionFilterProp="label"
          className="dynamic-documents-editor__use-plan"
          placeholder={USES.PRODUCTS_PLACEHOLDER}
          value={usage.insurancePlanIds}
          options={plans}
          getPopupContainer={popupContainer}
          onChange={(insurancePlanIds) =>
            onChange({ ...usage, insurancePlanIds })
          }
        />
        <button
          type="button"
          className="dynamic-documents-editor__link dynamic-documents-editor__link--danger"
          onClick={onRemove}
        >
          {USES.REMOVE}
        </button>
      </div>
      <p className="dynamic-documents-editor__muted">{USES.PRODUCTS_HINT}</p>
      <div className="static-documents__steps">
        {Object.values(DOCUMENT_STEPS).map((step) => (
          <Checkbox
            key={step}
            checked={usage.steps[step] === true}
            onChange={({ target: { checked } }) =>
              onChange({
                ...usage,
                steps: { ...usage.steps, [step]: checked },
              })
            }
          >
            {STEPS[step]}
          </Checkbox>
        ))}
      </div>
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: "advanced",
            label: USES.ADVANCED,
            children: (
              <div className="dynamic-documents-editor__use-advanced">
                <Checkbox
                  checked={usage.exactMatch}
                  onChange={({ target: { checked } }) =>
                    onChange({ ...usage, exactMatch: checked })
                  }
                >
                  {USES.EXACT_MATCH}
                </Checkbox>
                <p className="dynamic-documents-editor__muted">
                  {USES.EXACT_MATCH_HINT}
                </p>
                <Checkbox
                  checked={usage.ignoreOthers}
                  onChange={({ target: { checked } }) =>
                    onChange({ ...usage, ignoreOthers: checked })
                  }
                >
                  {USES.IGNORE_OTHERS}
                </Checkbox>
                <p className="dynamic-documents-editor__muted">
                  {USES.IGNORE_OTHERS_HINT}
                </p>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

const UsagesEditor = ({ usages, plans, onChange }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const USES = t("STATIC.USES", { returnObjects: true });

  return (
    <div className="dynamic-documents-editor__uses-editor">
      <h4 className="dynamic-documents-editor__step-title">{USES.TITLE}</h4>
      {isEmpty(usages) && (
        <p className="dynamic-documents-editor__muted">{USES.EMPTY}</p>
      )}
      {usages.map((usage) => (
        <UsageEditor
          key={usage.key}
          usage={usage}
          plans={plans}
          onChange={(next) =>
            onChange(
              usages.map((current) =>
                current.key === usage.key ? next : current,
              ),
            )
          }
          onRemove={() =>
            onChange(usages.filter((current) => current.key !== usage.key))
          }
        />
      ))}
      <button
        type="button"
        className="dynamic-documents-editor__link"
        onClick={() => onChange([...usages, newUsage()])}
      >
        {USES.ADD}
      </button>
    </div>
  );
};

const StaticUsageLine = ({
  usages,
  plans,
  planNames,
  open,
  onOpenChange,
  onChange,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.DYNAMIC_DOCUMENTS);
  const USES = t("STATIC.USES", { returnObjects: true });
  const complete = completeUsages(usages);
  const none = isEmpty(complete);
  const many = complete.length > LISTED_USAGES;

  const changeOpen = (next) => {
    if (next && isEmpty(usages)) onChange([newUsage()]);
    if (!next && complete.length !== usages.length) onChange(complete);
    onOpenChange(next);
  };

  const addUsage = () => {
    onChange([...usages, newUsage()]);
    onOpenChange(true);
  };

  return (
    <div className="dynamic-documents-editor__usage">
      {none ? (
        <span className="dynamic-documents-editor__usage-none">
          {USES.USED_IN} {USES.NONE}
        </span>
      ) : (
        <>
          <span className="dynamic-documents-editor__usage-label">
            {USES.USED_IN}
          </span>
          <strong
            className="dynamic-documents-editor__usage-summary"
            title={complete
              .map((usage) => usageText(usage, planNames))
              .join("\n")}
          >
            {many
              ? t("STATIC.USES.MANY", { count: complete.length })
              : complete
                  .map((usage) => usageText(usage, planNames))
                  .join(" ; ")}
          </strong>
        </>
      )}
      <Popover
        open={open}
        onOpenChange={changeOpen}
        trigger="click"
        placement="bottomLeft"
        overlayClassName="dynamic-documents-editor__uses-popover"
        content={
          <UsagesEditor usages={usages} plans={plans} onChange={onChange} />
        }
      >
        <button type="button" className="dynamic-documents-editor__link">
          {none ? USES.CHOOSE : many ? USES.SEE_EDIT : USES.EDIT}
        </button>
      </Popover>
      {!none && (
        <button
          type="button"
          className="dynamic-documents-editor__link"
          onClick={addUsage}
        >
          {USES.ADD}
        </button>
      )}
    </div>
  );
};

export default StaticUsageLine;
